import React, { useState, useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

interface Account {
  id: string;
  name: string;
  country?: string;
  timezone?: string;
  currency?: string;
  status: string;
  domains?: string[];
  ssoProviderId?: string;
  ssoProviderName?: string;
  requireSso: boolean;
  userCount?: number;
  createdAt: string;
  demandTerm?: string;
  supplyTerm?: string;
  hedgeTerm?: string;
  viewingHorizonMonths?: number;
  prorateCurrentMonthDemand?: boolean;
  reprorateDemandOnImport?: boolean;
  settings?: { import_workers?: string[]; [key: string]: any };
}

const AVAILABLE_WORKERS = [
  { name: 'strauss', label: 'Strauss' },
  { name: 'carmit', label: 'Carmit' },
  { name: 'admis-pdf', label: 'Hedges (ADMIS PDF)' },
  { name: 'israco', label: 'Israco (Iscar XLSX)' },
  { name: 'manual', label: 'Manual (Excel Template)' },
];

const EMAIL_INGEST_WORKERS = [
  { name: 'strauss', label: 'Strauss' },
  { name: 'carmit', label: 'Carmit' },
  { name: 'admis-pdf', label: 'Hedges (ADMIS PDF)' },
  { name: 'israco', label: 'Israco (Iscar XLSX)' },
  { name: 'manual', label: 'Manual (Excel Template)' },
];

interface EmailAlias {
  id: number;
  account_id: number;
  alias: string;
  worker_name: string;
  allowed_senders: string[] | null;
  active: boolean;
  notes: string | null;
}

interface NewAliasForm {
  alias: string;
  workerName: string;
  allowedSenders: string;
  notes: string;
}

interface CreateAccountForm {
  name: string;
  country: string;
  timezone: string;
  currency: string;
  status: string;
  domains: string;
  ssoProviderId: string;
  ssoProviderName: string;
  requireSso: boolean;
  demandTerm: string;
  supplyTerm: string;
  hedgeTerm: string;
}

interface EditAccountForm extends CreateAccountForm {
  id: string;
  importWorkers: string[];
  viewingHorizonMonths: number;
  prorateCurrentMonthDemand: boolean;
  reprorateDemandOnImport: boolean;
  freshnessGreenDays: number;
  freshnessOrangeDays: number;
}

const AccountsManagement: React.FC = () => {
  const { data: userData } = useUser();
  const navigate = useNavigate();
  
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const [createForm, setCreateForm] = useState<CreateAccountForm>({
    name: '',
    country: 'IL',
    timezone: 'Asia/Jerusalem',
    currency: 'USD',
    status: 'active',
    domains: '',
    ssoProviderId: '',
    ssoProviderName: '',
    requireSso: false,
    demandTerm: 'Demand',
    supplyTerm: 'Supply',
    hedgeTerm: 'Hedges',
  });
  
  const [editForm, setEditForm] = useState<EditAccountForm | null>(null);
  const [emailAliases, setEmailAliases] = useState<EmailAlias[]>([]);
  const [loadingAliases, setLoadingAliases] = useState(false);
  const [newAliasForm, setNewAliasForm] = useState<NewAliasForm>({ alias: '', workerName: '', allowedSenders: '', notes: '' });
  const [savingAlias, setSavingAlias] = useState(false);

  const loadEmailAliases = async (accountId: string) => {
    setLoadingAliases(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v2/email-ingest/aliases`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setEmailAliases((data.aliases || []).filter((a: EmailAlias) => String(a.account_id) === String(accountId)));
    } catch {
      setEmailAliases([]);
    } finally {
      setLoadingAliases(false);
    }
  };

  const createAlias = async (accountId: string) => {
    console.log('[createAlias] called', { accountId, alias: newAliasForm.alias, worker: newAliasForm.workerName });
    if (!newAliasForm.alias || !newAliasForm.workerName) return;
    setSavingAlias(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const senders = newAliasForm.allowedSenders.trim()
        ? newAliasForm.allowedSenders.split(',').map(s => s.trim()).filter(Boolean)
        : null;
      const res = await fetch(`${API_URL}/api/v2/email-ingest/aliases`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: Number(accountId), alias: newAliasForm.alias, workerName: newAliasForm.workerName, allowedSenders: senders, notes: newAliasForm.notes || null }),
      });
      const data = await res.json();
      console.log('[createAlias] response', res.status, data);
      if (!res.ok) {
        setMessage({ type: 'error', text: data.error || `Failed to create alias (${res.status})` });
        return;
      }
      setMessage({ type: 'success', text: `Alias ${newAliasForm.alias}@spotlock.co created!` });
      setNewAliasForm({ alias: '', workerName: '', allowedSenders: '', notes: '' });
      await loadEmailAliases(accountId);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to create alias' });
    } finally {
      setSavingAlias(false);
    }
  };

  const toggleAlias = async (aliasId: number, active: boolean, accountId: string) => {
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v2/email-ingest/aliases/${aliasId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) {
        const data = await res.json();
        setMessage({ type: 'error', text: data.error || 'Failed to update alias' });
        return;
      }
      await loadEmailAliases(accountId);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to update alias' });
    }
  };

  const deleteAlias = async (aliasId: number, accountId: string) => {
    if (!window.confirm('Delete this email alias?')) return;
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v2/email-ingest/aliases/${aliasId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        setMessage({ type: 'error', text: data.error || 'Failed to delete alias' });
        return;
      }
      await loadEmailAliases(accountId);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message || 'Failed to delete alias' });
    }
  };

  // Check if user is superadmin
  useEffect(() => {
    if (userData && !userData.isAdmin) {
      navigate('/');
    }
  }, [userData, navigate]);

  // Fetch accounts
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      
      const token = await user.getIdToken();
      
      const response = await fetch(`${API_URL}/api/v2/accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', response.status, errorData);
        throw new Error(errorData.error || `Failed to fetch accounts (${response.status})`);
      }

      const data = await response.json();
      setAccounts(data.accounts || []);
    } catch (error: any) {
      console.error('Error fetching accounts:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load accounts' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.isAdmin) {
      fetchAccounts();
    }
  }, [userData]);

  // Create account
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!createForm.name.trim()) {
      setMessage({ type: 'error', text: 'Account name is required' });
      return;
    }

    setProcessing(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const token = await user.getIdToken();

      const domainsArray = createForm.domains
        .split(',')
        .map(d => d.trim())
        .filter(d => d.length > 0);

      const response = await fetch(`${API_URL}/api/v2/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: createForm.name,
          country: createForm.country,
          timezone: createForm.timezone,
          currency: createForm.currency,
          status: createForm.status,
          domains: domainsArray.length > 0 ? domainsArray : undefined,
          ssoProviderId: createForm.ssoProviderId || undefined,
          ssoProviderName: createForm.ssoProviderName || undefined,
          requireSso: createForm.requireSso,
          demandTerm: createForm.demandTerm || 'Demand',
          supplyTerm: createForm.supplyTerm || 'Supply',
          hedgeTerm: createForm.hedgeTerm || 'Hedges',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create account');
      }

      setMessage({ type: 'success', text: 'Account created successfully!' });
      setShowCreateModal(false);
      setCreateForm({
        name: '',
        country: 'IL',
        timezone: 'Asia/Jerusalem',
        currency: 'USD',
        status: 'active',
        domains: '',
        ssoProviderId: '',
        ssoProviderName: '',
        requireSso: false,
        demandTerm: 'Demand',
        supplyTerm: 'Supply',
        hedgeTerm: 'Hedges',
      });
      fetchAccounts();
    } catch (error: any) {
      console.error('Error creating account:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to create account' });
    } finally {
      setProcessing(false);
    }
  };

  // Update account
  const handleUpdateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm) return;

    setProcessing(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const token = await user.getIdToken();

      const domainsArray = editForm.domains
        .split(',')
        .map(d => d.trim())
        .filter(d => d.length > 0);

      const response = await fetch(`${API_URL}/api/v2/accounts/${editForm.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editForm.name,
          country: editForm.country,
          timezone: editForm.timezone,
          currency: editForm.currency,
          status: editForm.status,
          domains: domainsArray.length > 0 ? domainsArray : undefined,
          ssoProviderId: editForm.ssoProviderId || undefined,
          ssoProviderName: editForm.ssoProviderName || undefined,
          requireSso: editForm.requireSso,
          demandTerm: editForm.demandTerm || 'Demand',
          supplyTerm: editForm.supplyTerm || 'Supply',
          hedgeTerm: editForm.hedgeTerm || 'Hedges',
          viewingHorizonMonths: editForm.viewingHorizonMonths || 12,
          prorateCurrentMonthDemand: editForm.prorateCurrentMonthDemand,
          reprorateDemandOnImport: editForm.prorateCurrentMonthDemand ? editForm.reprorateDemandOnImport : false,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update account');
      }

      // Also save import_workers to account settings JSONB
      const settingsResponse = await fetch(`${API_URL}/api/v2/accounts/${editForm.id}/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          settings: {
            import_workers: editForm.importWorkers.length > 0 ? editForm.importWorkers : null,
            dataFreshnessThresholds: { greenDays: editForm.freshnessGreenDays, orangeDays: editForm.freshnessOrangeDays },
          },
        }),
      });
      if (!settingsResponse.ok) {
        console.warn('Failed to save import_workers setting');
      }

      setMessage({ type: 'success', text: 'Account updated successfully!' });
      setShowEditModal(false);
      setEditForm(null);
      fetchAccounts();
    } catch (error: any) {
      console.error('Error updating account:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to update account' });
    } finally {
      setProcessing(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async (accountId: string, accountName: string) => {
    if (!window.confirm(`Are you sure you want to delete account "${accountName}"? This action cannot be undone.`)) {
      return;
    }

    setProcessing(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Not authenticated');
      }
      const token = await user.getIdToken();

      const response = await fetch(`${API_URL}/api/v2/accounts/${accountId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete account');
      }

      setMessage({ type: 'success', text: 'Account deleted successfully!' });
      fetchAccounts();
    } catch (error: any) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete account' });
    } finally {
      setProcessing(false);
    }
  };

  if (!userData?.isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Accounts Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Create Account
        </button>
      </div>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading accounts...</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Users
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SSO
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Domains
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {accounts.map((account) => (
                <tr key={account.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{account.name}</div>
                    <div className="text-xs text-gray-500">
                      {account.country} • {account.currency}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        account.status === 'active'
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : account.status === 'inactive'
                          ? 'bg-gray-100 text-gray-800 border border-gray-200'
                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                      }`}
                    >
                      {account.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {account.userCount || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {account.requireSso ? (
                      <div className="text-sm">
                        <div className="font-medium text-blue-600">✓ Required</div>
                        <div className="text-xs text-gray-500">{account.ssoProviderName}</div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Optional</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {account.domains && account.domains.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {account.domains.map((domain, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                              {domain}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No domains</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setEditForm({
                          id: account.id,
                          name: account.name,
                          country: account.country || 'IL',
                          timezone: account.timezone || 'Asia/Jerusalem',
                          currency: account.currency || 'USD',
                          status: account.status,
                          domains: account.domains?.join(', ') || '',
                          ssoProviderId: account.ssoProviderId || '',
                          ssoProviderName: account.ssoProviderName || '',
                          requireSso: account.requireSso,
                          demandTerm: account.demandTerm || 'Demand',
                          supplyTerm: account.supplyTerm || 'Supply',
                          hedgeTerm: account.hedgeTerm || 'Hedges',
                          importWorkers: account.settings?.import_workers || [],
                          viewingHorizonMonths: account.viewingHorizonMonths || 12,
                          prorateCurrentMonthDemand: account.prorateCurrentMonthDemand || false,
                          reprorateDemandOnImport: account.reprorateDemandOnImport || false,
                          freshnessGreenDays: account.settings?.dataFreshnessThresholds?.greenDays || 7,
                          freshnessOrangeDays: account.settings?.dataFreshnessThresholds?.orangeDays || 14,
                        });
                        setEmailAliases([]);
                        setNewAliasForm({ alias: '', workerName: '', allowedSenders: '', notes: '' });
                        loadEmailAliases(account.id);
                        setShowEditModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteAccount(account.id, account.name)}
                      className="text-red-600 hover:text-red-900"
                      disabled={processing}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {accounts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No accounts found. Create your first account to get started.
            </div>
          )}
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Create New Account</h2>
              <form onSubmit={handleCreateAccount}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Name *
                    </label>
                    <input
                      type="text"
                      value={createForm.name}
                      onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <select
                        value={createForm.country}
                        onChange={(e) => setCreateForm({ ...createForm, country: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="IL">Israel</option>
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={createForm.currency}
                        onChange={(e) => setCreateForm({ ...createForm, currency: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="ILS">ILS</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={createForm.status}
                        onChange={(e) => setCreateForm({ ...createForm, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Domains (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={createForm.domains}
                      onChange={(e) => setCreateForm({ ...createForm, domains: e.target.value })}
                      placeholder="example.com, example.co.il"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Domains used for SSO and user email validation
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Custom Terminology</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Customize the terms used for the three core categories throughout the application
                    </p>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Demand Term
                        </label>
                        <input
                          type="text"
                          value={createForm.demandTerm}
                          onChange={(e) => setCreateForm({ ...createForm, demandTerm: e.target.value })}
                          placeholder="Demand"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supply Term
                        </label>
                        <input
                          type="text"
                          value={createForm.supplyTerm}
                          onChange={(e) => setCreateForm({ ...createForm, supplyTerm: e.target.value })}
                          placeholder="Supply"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hedge Term
                        </label>
                        <input
                          type="text"
                          value={createForm.hedgeTerm}
                          onChange={(e) => setCreateForm({ ...createForm, hedgeTerm: e.target.value })}
                          placeholder="Hedges"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">SSO Configuration</h3>
                    
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={createForm.requireSso}
                          onChange={(e) => setCreateForm({ ...createForm, requireSso: e.target.checked })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Require SSO Authentication</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6 mt-1">
                        If enabled, users must authenticate via SSO (no email/password or Google)
                      </p>
                    </div>

                    {createForm.requireSso && (
                      <div className="space-y-3 ml-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SSO Provider ID
                          </label>
                          <input
                            type="text"
                            value={createForm.ssoProviderId}
                            onChange={(e) => setCreateForm({ ...createForm, ssoProviderId: e.target.value })}
                            placeholder="saml.microsoftonline.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Firebase SAML provider ID (e.g., saml.microsoftonline.com)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SSO Provider Name
                          </label>
                          <input
                            type="text"
                            value={createForm.ssoProviderName}
                            onChange={(e) => setCreateForm({ ...createForm, ssoProviderName: e.target.value })}
                            placeholder="Strauss SSO"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Display name shown to users
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={processing}
                  >
                    {processing ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && editForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Edit Account</h2>
              <form onSubmit={handleUpdateAccount}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Name *
                    </label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                      <select
                        value={editForm.country}
                        onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="IL">Israel</option>
                        <option value="US">United States</option>
                        <option value="UK">United Kingdom</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                      <select
                        value={editForm.currency}
                        onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                        <option value="ILS">ILS</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Domains (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={editForm.domains}
                      onChange={(e) => setEditForm({ ...editForm, domains: e.target.value })}
                      placeholder="example.com, example.co.il"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Domains used for SSO and user email validation
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Custom Terminology</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Customize the terms used for the three core categories throughout the application
                    </p>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Demand Term
                        </label>
                        <input
                          type="text"
                          value={editForm.demandTerm}
                          onChange={(e) => setEditForm({ ...editForm, demandTerm: e.target.value })}
                          placeholder="Demand"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supply Term
                        </label>
                        <input
                          type="text"
                          value={editForm.supplyTerm}
                          onChange={(e) => setEditForm({ ...editForm, supplyTerm: e.target.value })}
                          placeholder="Supply"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hedge Term
                        </label>
                        <input
                          type="text"
                          value={editForm.hedgeTerm}
                          onChange={(e) => setEditForm({ ...editForm, hedgeTerm: e.target.value })}
                          placeholder="Hedges"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Exposure & Data Settings</h3>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Viewing Horizon (months)
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min={1}
                          max={36}
                          value={editForm.viewingHorizonMonths}
                          onChange={(e) => setEditForm({ ...editForm, viewingHorizonMonths: Math.max(1, Math.min(36, parseInt(e.target.value) || 12)) })}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-500">months forward</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        How many months of positions to include in charts, exposure calculations, and strategy views
                      </p>
                    </div>

                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.prorateCurrentMonthDemand}
                          onChange={(e) => setEditForm({ ...editForm, prorateCurrentMonthDemand: e.target.checked })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Pro-rate current month demand</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6 mt-1">
                        When enabled, current-month demand quantities are adjusted by the fraction of the month remaining at import time.
                        For example, importing on the 15th of a 30-day month reduces current-month demand by ~50%.
                      </p>

                      {editForm.prorateCurrentMonthDemand && (
                        <div className="mt-3 ml-6">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={editForm.reprorateDemandOnImport}
                              onChange={(e) => setEditForm({ ...editForm, reprorateDemandOnImport: e.target.checked })}
                              className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">Re-prorate on each supply/inventory import</span>
                          </label>
                          <p className="text-xs text-gray-500 ml-6 mt-1">
                            For accounts that update demand rarely but re-import POs/inventory often. Each time a
                            supply/inventory import is approved, current-month demand is re-prorated using that import&apos;s date
                            (the original quantity is preserved). Without this, proration is applied only once, at demand import time.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Data Freshness Thresholds
                      </label>
                      <div className="flex items-center gap-3">
                        <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-xs text-gray-500 w-16">Green ≤</span>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={editForm.freshnessGreenDays}
                          onChange={(e) => setEditForm({ ...editForm, freshnessGreenDays: Math.max(1, parseInt(e.target.value) || 7) })}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500">days</span>
                        <span className="mx-2 inline-block w-3 h-3 rounded-full bg-orange-400" />
                        <span className="text-xs text-gray-500 w-16">Orange ≤</span>
                        <input
                          type="number"
                          min={1}
                          max={90}
                          value={editForm.freshnessOrangeDays}
                          onChange={(e) => setEditForm({ ...editForm, freshnessOrangeDays: Math.max(1, parseInt(e.target.value) || 14) })}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="text-xs text-gray-500">days</span>
                        <span className="mx-2 inline-block w-3 h-3 rounded-full bg-gray-400" />
                        <span className="text-xs text-gray-500">Gray after</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Colored dot shown next to account name in the TopBar. Indicates how recently data was imported.
                      </p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Import Settings</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Select which import workers are available for this account
                    </p>
                    <div className="flex gap-4">
                      {AVAILABLE_WORKERS.map((w) => (
                        <label key={w.name} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editForm.importWorkers.includes(w.name)}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...editForm.importWorkers, w.name]
                                : editForm.importWorkers.filter((n) => n !== w.name);
                              setEditForm({ ...editForm, importWorkers: next });
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm font-medium text-gray-700">{w.label}</span>
                        </label>
                      ))}
                    </div>
                    {editForm.importWorkers.length === 0 && (
                      <p className="text-xs text-orange-500 mt-1">No workers selected — all workers will be shown</p>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">Email Auto-Ingestion</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Configure inbound email aliases that route client files to this account. Clients email data files to <span className="font-mono text-xs bg-gray-100 px-1 rounded">alias@spotlock.co</span>.
                    </p>

                    {loadingAliases ? (
                      <p className="text-sm text-gray-400">Loading aliases...</p>
                    ) : (
                      <>
                        {emailAliases.length > 0 && (
                          <div className="mb-3 space-y-2">
                            {emailAliases.map(alias => (
                              <div key={alias.id} className={`flex items-start gap-2 p-2 rounded-lg border text-sm ${alias.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono font-medium text-blue-700">{alias.alias}@spotlock.co</span>
                                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{EMAIL_INGEST_WORKERS.find(w => w.name === alias.worker_name)?.label || alias.worker_name}</span>
                                    {!alias.active && <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">inactive</span>}
                                  </div>
                                  {alias.allowed_senders && alias.allowed_senders.length > 0 && (
                                    <div className="text-xs text-gray-500 mt-0.5">Allowed senders: {alias.allowed_senders.join(', ')}</div>
                                  )}
                                  {alias.notes && <div className="text-xs text-gray-400 mt-0.5">{alias.notes}</div>}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => toggleAlias(alias.id, !alias.active, editForm.id)}
                                    className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-100"
                                  >
                                    {alias.active ? 'Disable' : 'Enable'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteAlias(alias.id, editForm.id)}
                                    className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="bg-gray-50 rounded-lg p-3 border border-dashed border-gray-300">
                          <p className="text-xs font-medium text-gray-600 mb-2">Add new alias</p>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="text-xs text-gray-500">Alias (local part)</label>
                              <div className="flex items-center">
                                <input
                                  type="text"
                                  value={newAliasForm.alias}
                                  onChange={e => setNewAliasForm({ ...newAliasForm, alias: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                                  placeholder="israco"
                                  className="w-full px-2 py-1 border border-gray-300 rounded-l text-sm focus:ring-1 focus:ring-blue-500"
                                />
                                <span className="px-2 py-1 bg-gray-200 border border-l-0 border-gray-300 rounded-r text-xs text-gray-500 whitespace-nowrap">@spotlock.co</span>
                              </div>
                            </div>
                            <div>
                              <label className="text-xs text-gray-500">Worker</label>
                              <select
                                value={newAliasForm.workerName}
                                onChange={e => setNewAliasForm({ ...newAliasForm, workerName: e.target.value })}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="">Select worker...</option>
                                {EMAIL_INGEST_WORKERS.map(w => (
                                  <option key={w.name} value={w.name}>{w.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="mb-2">
                            <label className="text-xs text-gray-500">Allowed senders (comma-separated emails/domains, empty = accept all)</label>
                            <input
                              type="text"
                              value={newAliasForm.allowedSenders}
                              onChange={e => setNewAliasForm({ ...newAliasForm, allowedSenders: e.target.value })}
                              placeholder="qvt@tivtaam.co.il, @tivtaam.co.il"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <div className="mb-2">
                            <label className="text-xs text-gray-500">Notes (optional)</label>
                            <input
                              type="text"
                              value={newAliasForm.notes}
                              onChange={e => setNewAliasForm({ ...newAliasForm, notes: e.target.value })}
                              placeholder="e.g. Israco weekly XLSX"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => createAlias(editForm.id)}
                            disabled={!newAliasForm.alias || !newAliasForm.workerName || savingAlias}
                            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {savingAlias ? 'Adding...' : 'Add Alias'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <h3 className="text-lg font-semibold mb-3">SSO Configuration</h3>
                    
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editForm.requireSso}
                          onChange={(e) => setEditForm({ ...editForm, requireSso: e.target.checked })}
                          className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Require SSO Authentication</span>
                      </label>
                      <p className="text-xs text-gray-500 ml-6 mt-1">
                        If enabled, users must authenticate via SSO (no email/password or Google)
                      </p>
                    </div>

                    {editForm.requireSso && (
                      <div className="space-y-3 ml-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SSO Provider ID
                          </label>
                          <input
                            type="text"
                            value={editForm.ssoProviderId}
                            onChange={(e) => setEditForm({ ...editForm, ssoProviderId: e.target.value })}
                            placeholder="saml.microsoftonline.com"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Firebase SAML provider ID (e.g., saml.microsoftonline.com)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            SSO Provider Name
                          </label>
                          <input
                            type="text"
                            value={editForm.ssoProviderName}
                            onChange={(e) => setEditForm({ ...editForm, ssoProviderName: e.target.value })}
                            placeholder="Strauss SSO"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Display name shown to users
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditForm(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={processing}
                  >
                    {processing ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsManagement;
